import { memo } from 'react';
import { css, Theme, useTheme } from "@emotion/react";

import { NOOP } from "core/constants";
import { CheckoutStep } from "pages/Components/CartAndCheckout/CheckoutPage";

type Props = {
    steps: CheckoutStep[],
    currentStep: number,
    onNext: () => void,
    onBack: () => void,
    onStepClick: (step: number) => void,
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CheckoutPagination = ({ steps, currentStep, onNext = NOOP, onBack, onStepClick } : Props) => {
    const theme: Theme = useTheme();

    const circleStyle = ({ isCurrent = false, isCompleted = false }) => css`
        width: 23px;
        height: 23px;
        background: ${isCompleted ? theme.colours.curiousBlue2 : 'white'};
        border-radius: 50%;
        border: 3px solid ${theme.colours[isCurrent || isCompleted ? 'curiousBlue2' : 'silver5']};
        scale: ${isCurrent ? 1.25 : 1};
    `;

    const lineStyle = ({ hasCompleted = false}) => css`
        width: 129px;
        height: 3px;
        background: ${theme.colours[hasCompleted ? 'curiousBlue2' : 'silver5']};
        
        ${theme.breakpoints.down('xs')}  {
            width: 55px;
        }
    `;

    const indicatorsStyle = css`
        display: flex;
        
        ${theme.breakpoints.down('xs')} {
            padding-left: 15px;
        }
    `;

    const indicatorContainerStyle = ({ isActive = false, isNext = false }) => css`
        display: flex;
        flex-direction: column;
        
        ${isActive ? css`
            cursor: pointer;
        ` : css`
            pointer-events: none;
        `};

        ${isNext && css`
            :hover {
                .label-step {
                    color: ${theme.colours.emperor};
                }

                .circle-step {
                    border: 3px solid ${theme.colours.emperor};
                }
            }
        `};
    `;

    const indicatorStepStyle = css`
        display: flex;
        align-items: center;
    `;

    const containerStyle = css`
        padding-top: 53px;
        display: flex;
        justify-content: center;
        align-items: center;
        
        ${theme.breakpoints.only('xl')} {
            padding-top: 22px;
        }

        ${theme.breakpoints.only('lg')} {
            padding-top: 26px;
        }

        ${theme.breakpoints.only('md')} {
            padding-top: 30px;
            padding-bottom: 50px;
        }

        ${theme.breakpoints.only('sm')} {
            padding-top: 21px;
            padding-bottom: 38px;
        }

        ${theme.breakpoints.down('xs')} {
            padding-top: 42px;
            padding-bottom: 24px;
        }
    `;

    const stepLabelStyle = ({ hasHighlight = false }) => css`
        position: relative;
        transform: translateX(-50%);
        left: 11.5px;
        text-align: center;
        color: ${theme.colours[hasHighlight ? 'curiousBlue2' : 'gray3']};
        
        ${theme.breakpoints.down('xs')} {
            max-width: 55px;
        };
    `;

    return (
        <div css={containerStyle}>
            <div>
                <div css={indicatorsStyle}>
                    {steps.map((step, index) => {
                        if (step.isHidden) return null;

                        const isCurrent = currentStep === index;
                        const isNext = currentStep + 1 === index;
                        const isPrevious = currentStep - 1 === index;
                        return (
                            <div
                                key={index}
                                css={indicatorContainerStyle({ isNext: isNext, isActive: onNext !== NOOP && (isNext || isPrevious) })}
                                onClick={isNext ? onNext : () => onStepClick(index)}
                            >
                                <div css={indicatorStepStyle}>
                                    <div
                                        css={circleStyle({ isCurrent, isCompleted: index < currentStep })}
                                        className={'circle-step'}
                                    />
                                    {index < steps.length - 1 && (
                                        <div css={lineStyle({ hasCompleted: index < currentStep })} />
                                    )}
                                </div>
                                <div
                                    css={stepLabelStyle({ hasHighlight: index < currentStep || isCurrent })}
                                    className={'label-step'}
                                >
                                    {step.label}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
};

export default memo(CheckoutPagination);